import {config} from '../config/config';

const baseURL = config.apiUrl;

interface RelatedCarItem {
    chassisNo: string;
    make: string;
    model: string;
    manualMakeModel?: string;
    relationshipType: string;
}

interface CarItem {
    position: number;
    dealer: string;
    port: string;
    orderNo?: string;
    consignee: string;
    chassisNo: string;
    make: string;
    model: string;
    manualMakeModel?: string;
    type: string;
    collect?: number | null;
    relatedCars?: RelatedCarItem[];
}

interface InventoryListRequest {
    companyName?: string;
    companyAddress?: string;
    voyageNo: string;
    date: string;
    shipName: string;
    loadingPort: string;
    shipperName?: string;
    cars: CarItem[];
    title?: string;
    showCollect?: boolean;
    includeRelated?: boolean;
}

interface StatRow {
    code: string;
    name: string;
    total: number;
    collect: number;
    prepaid: number;
    car: number;
    van: number;
    bigVan: number;
    truck: number;
}

interface VoyageStatisticsRequest {
    voyageCode: string;
    vesselName: string;
    date: string;
    viewType: 'shippers' | 'ports';
    data: StatRow[];
}

interface LineMeterStatRow {
    code: string;
    name: string;
    units: number;
    length: number;
    weight: number;
    cbm: number;
}

interface LineMeterStatisticsRequest {
    voyageCode: string;
    vesselName: string;
    date: string;
    data: LineMeterStatRow[];
}

interface ManifestCarItem {
    blNo: string;
    consignee: string;
    chassisNo: string;
    packages: number;
    contents: string;
    weightKg: number;
    measurementM3: number;
    freight: string;
}

interface ShipperGroup {
    shipperId: string;
    shipperName?: string;
    cars: ManifestCarItem[];
}

interface FreightManifestRequest {
    companyName?: string;
    companyAddress?: string;
    vesselName: string;
    date: string;
    voyageNo: string;
    fromPort: string;
    toPort: string;
    shipperGroups: ShipperGroup[];
    pageNo?: number;
    includeRelated?: boolean;
    manifestType?: 'freight' | 'cargo';
}

interface BillOfLadingRequest {
    blNumber: string;
    refNumber: string;
    voyageNumber?: string;
    shipper: {
        name: string;
        address?: string;
    };
    consignee: {
        name: string;
        address?: string;
    };
    notifyAddress?: string;
    preCarriagePlace?: string;
    vesselName: string;
    portOfLoading: string;
    portOfDischarge: string;
    deliveryPlace?: string;
    marksAndNumbers: string;
    numberOfUnits: string;
    description: string;
    grossWeight: string;
    measurement: string;
    freightPayableAt: string;
    placeOfIssue: string;
    dateOfIssue: string;
    numberOfOriginalBLs: string;
    isOriginal: boolean;
    loaded: boolean;
}

interface CombinedBillOfLadingRequest {
    bills: BillOfLadingRequest[];
}

interface AdditionalFeeValue {
    amount: number;
    type: 'F' | 'C' | 'B';
}

interface InvoiceRelatedCarItem {
    chassisNo: string;
    make: string;
    model: string;
    manualMakeModel?: string;
    relationshipType: string;
    additionalFees: Record<string, AdditionalFeeValue>;
}

interface InvoiceCarItem {
    position: number;
    chassisNo: string;
    make: string;
    model: string;
    manualMakeModel?: string;
    port: string;
    length: number | null;
    carType: string;
    freight: number;
    collect: number;
    additionalFees: Record<string, AdditionalFeeValue>;
    relatedCars?: InvoiceRelatedCarItem[];
}

interface InvoiceRequest {
    companyName?: string;
    companyAddress?: string;
    invoiceNumber: string;
    date: string;
    voyageNo: string;
    vesselName: string;
    shipperCode: string;
    shipperName: string;
    shipperAddress?: string;
    portFilter?: string;
    pol?: string;
    cars: InvoiceCarItem[];
    includeRelated?: boolean;
}

interface BookingConfirmationRelatedCar {
    relationshipType: string;
    makeModel: string;
    vinSerial: string;
}

interface BookingConfirmationCarItem {
    nr: number;
    description: string;
    vinSerial: string;
    destination: string;
    relatedCars?: BookingConfirmationRelatedCar[];
}

interface BookingConfirmationRequest {
    companyName?: string;
    companyAddress?: string;
    bookingNumber?: string;
    terminal: string;
    date: string;
    pol: string;
    terminalAddress?: string;
    cars: BookingConfirmationCarItem[];
    portRemarks?: string;
}

async function downloadPdf(url: string, data: any, filename: string): Promise<void> {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${baseURL}${url}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? {'Authorization': `Bearer ${token}`} : {}),
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw new Error('Failed to generate PDF');
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
}

async function getPdfBlob(url: string, data: any): Promise<Blob> {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${baseURL}${url}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? {'Authorization': `Bearer ${token}`} : {}),
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw new Error('Failed to generate PDF');
    }

    return response.blob();
}

export const printService = {
    async generateInventoryList(data: InventoryListRequest, filename: string): Promise<void> {
        await downloadPdf('/prints/inventory-list', data, filename);
    },

    async getInventoryListBlob(data: InventoryListRequest): Promise<Blob> {
        return getPdfBlob('/prints/inventory-list', data);
    },

    async generateVoyageStatistics(data: VoyageStatisticsRequest, filename: string): Promise<void> {
        await downloadPdf('/prints/voyage-statistics', data, filename);
    },

    async generateLineMeterStatistics(data: LineMeterStatisticsRequest, filename: string): Promise<void> {
        await downloadPdf('/prints/line-meter-statistics', data, filename);
    },

    async generateFreightManifest(data: FreightManifestRequest, filename: string): Promise<void> {
        await downloadPdf('/prints/freight-manifest', data, filename);
    },

    async generateBillOfLading(data: BillOfLadingRequest, filename: string): Promise<void> {
        await downloadPdf('/prints/bill-of-lading', data, filename);
    },

    async getBillOfLadingBlob(data: BillOfLadingRequest): Promise<Blob> {
        return getPdfBlob('/prints/bill-of-lading', data);
    },

    async generateCombinedBillOfLading(data: CombinedBillOfLadingRequest, filename: string): Promise<void> {
        await downloadPdf('/prints/combined-bill-of-lading', data, filename);
    },

    async getCombinedBillOfLadingBlob(data: CombinedBillOfLadingRequest): Promise<Blob> {
        return getPdfBlob('/prints/combined-bill-of-lading', data);
    },

    async generateInvoice(data: InvoiceRequest, filename: string): Promise<void> {
        await downloadPdf('/prints/invoice', data, filename);
    },

    async getOrCreateInvoiceNumber(voyageId: string, shipperId: string, shipperCode: string, port: string, totalAmount: number): Promise<{
        invoiceNumber: string;
        isNew: boolean
    }> {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${baseURL}/prints/invoice-number`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? {'Authorization': `Bearer ${token}`} : {}),
            },
            body: JSON.stringify({voyageId, shipperId, shipperCode, port, totalAmount}),
        });

        if (!response.ok) {
            throw new Error('Failed to get invoice number');
        }

        return response.json();
    },

    async generateBookingConfirmation(data: BookingConfirmationRequest, filename: string): Promise<void> {
        await downloadPdf('/prints/booking-confirmation', data, filename);
    },
};

